import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { TGALoader } from 'three/examples/jsm/loaders/TGALoader.js';
import { resolveTextureUrl, preprocessTextureName } from './textureResolvers.js';

/**
 * Represents a loaded file (model file or texture) with its data
 */
export type LoadedFileData = {
  filename: string;
  data: ArrayBuffer | Blob;
  mimeType?: string;
};

/**
 * Texture tracking data captured during loading
 */
export type TextureLoadingInfo = {
  requestedTextures: string[];
  loadedTextures: string[];
  failedTextures: string[];
  textureCache: Map<string, THREE.Texture>;
};

export type LoadResult = {
  boundingBox: THREE.Box3;
  object: THREE.Group;
  staticObject: THREE.Group; // Non-skinned copy for static display
  animations: THREE.AnimationClip[]; // Available animations
  hasMeshes: boolean;
  hasSkinnedMesh: boolean; // Whether the model contains any skinned meshes
  removedTextures: boolean;
  missingTextures: string[]; // List of texture filenames that failed to load
  textureUrls: Map<string, string>; // Maps texture UUID to file URL
  requestedTextures: string[]; // All textures that were requested during loading
  loadedTextures: string[]; // Textures that loaded successfully
  failedTextures: string[]; // Textures that failed to load (from loader, not material check)
  orphanedTextures?: Map<string, THREE.Texture>;
  loaderWarnings: string[]; // Warnings from the FBX loader (unsupported features, etc.)
};

/**
 * Store for tracking loaded files (model files and textures) during model loading
 */
class LoadedFilesStore {
  private files: Map<string, LoadedFileData> = new Map();
  fileUrlCache: Map<string, string> = new Map(); // Maps filename to download URL

  /**
   * Add a file to the store
   */
  addFile(filename: string, data: ArrayBuffer | Blob, mimeType?: string): void {
    this.files.set(filename, { filename, data, mimeType });
  }

  /**
   * Get all loaded files
   */
  getFiles(): LoadedFileData[] {
    return Array.from(this.files.values());
  }

  /**
   * Get file URL for a specific file
   */
  getFileUrl(filename: string): string | undefined {
    return this.fileUrlCache.get(filename);
  }

  /**
   * Clear all files
   */
  clear(): void {
    this.files.clear();
    this.fileUrlCache.clear();
  }

  /**
   * Check if a file exists
   */
  hasFile(filename: string): boolean {
    return this.files.has(filename);
  }
}

/**
 * Global store instance for loaded files
 */
export const loadedFilesStore = new LoadedFilesStore();

type TextureCaptureContext = {
  textureInfo: TextureLoadingInfo;
  loggedUrls: Set<string>;
};

const TEXTURE_LOADER_PATCH_MARKER = '__enhancedFbxTexturePatched';
const TEXTURE_LOADER_ORIGINAL_MARKER = '__enhancedFbxTextureOriginalLoad';
let activeTextureCaptureContext: TextureCaptureContext | null = null;

function installTextureLoaderHookOnce() {
  const textureLoaderProto = THREE.TextureLoader
    .prototype as typeof THREE.TextureLoader.prototype & {
    [TEXTURE_LOADER_PATCH_MARKER]?: boolean;
    [TEXTURE_LOADER_ORIGINAL_MARKER]?: typeof THREE.TextureLoader.prototype.load;
  };

  if (textureLoaderProto[TEXTURE_LOADER_PATCH_MARKER]) {
    return;
  }

  const originalLoad = textureLoaderProto.load;
  textureLoaderProto[TEXTURE_LOADER_ORIGINAL_MARKER] = originalLoad;

  textureLoaderProto.load = function (
    this: THREE.TextureLoader,
    ...args: Parameters<typeof originalLoad>
  ): ReturnType<typeof originalLoad> {
    const [url, onLoad, onProgress, onError] = args;
    const context = activeTextureCaptureContext;

    if (!context) {
      return originalLoad.call(this, ...args);
    }

    if (!context.loggedUrls.has(url)) {
      console.debug('[TextureLoader] Loading texture from:', url);
      context.loggedUrls.add(url);
    }

    const urlObj = new URL(url, window.location.href);
    let filename = decodeURIComponent(urlObj.pathname.split('/').pop() || url);
    filename = preprocessTextureName(filename);

    if (!context.textureInfo.requestedTextures.includes(filename)) {
      context.textureInfo.requestedTextures.push(filename);
    }

    const texture = originalLoad.call(
      this,
      url,
      (tex) => {
        if (!context.textureInfo.loadedTextures.includes(filename)) {
          context.textureInfo.loadedTextures.push(filename);
        }

        tex.userData = tex.userData || {};
        tex.userData['url'] = url;
        tex.userData['filename'] = filename;

        context.textureInfo.textureCache.set(url, tex);
        context.textureInfo.textureCache.set(filename, tex);

        if (onLoad) onLoad(tex);
      },
      onProgress,
      (err) => {
        console.debug('[TextureLoader] Error loading texture:', url, err);
        if (!context.textureInfo.failedTextures.includes(filename)) {
          context.textureInfo.failedTextures.push(filename);
        }
        if (onError) onError(err);
      },
    );

    context.textureInfo.textureCache.set(url, texture);
    context.textureInfo.textureCache.set(filename, texture);

    return texture;
  };

  textureLoaderProto[TEXTURE_LOADER_PATCH_MARKER] = true;
}

function isNameMatch(meshName: string, textureName: string): boolean {
  const normMesh = meshName.toLowerCase().replace(/[^a-z0-9]/g, '');
  const normTex = textureName.toLowerCase().replace(/[^a-z0-9]/g, '');
  // Проверяем вхождение имени (например "Body" входит в "Char_Body_Diff.jpg")
  return normTex.includes(normMesh) || normMesh.includes(normTex);
}

/**
 * Checks if an object hierarchy contains any skinned meshes
 */
function hasSkinnedMeshes(object: THREE.Object3D): boolean {
  let found = false;
  object.traverse((child) => {
    if (child instanceof THREE.SkinnedMesh) {
      found = true;
    }
  });
  return found;
}

/**
 * Converts a SkinnedMesh to a regular Mesh by baking the current pose.
 * This removes all skeletal animation dependencies.
 */
function convertSkinnedMeshToMesh(skinnedMesh: THREE.SkinnedMesh): THREE.Mesh {
  // Clone the geometry
  const geometry = skinnedMesh.geometry.clone();

  // Clone the material(s)
  const material = Array.isArray(skinnedMesh.material)
    ? skinnedMesh.material.map((m) => m.clone())
    : skinnedMesh.material.clone();

  // Create a regular Mesh
  const mesh = new THREE.Mesh(geometry, material);

  // Copy transform
  mesh.name = skinnedMesh.name;
  mesh.position.copy(skinnedMesh.position);
  mesh.rotation.copy(skinnedMesh.rotation);
  mesh.scale.copy(skinnedMesh.scale);
  mesh.matrix.copy(skinnedMesh.matrix);
  mesh.matrixWorld.copy(skinnedMesh.matrixWorld);
  mesh.castShadow = skinnedMesh.castShadow;
  mesh.receiveShadow = skinnedMesh.receiveShadow;
  mesh.visible = skinnedMesh.visible;
  mesh.userData = { ...skinnedMesh.userData };

  return mesh;
}

/**
 * Creates a static (non-skinned) copy of the object hierarchy.
 * SkinnedMeshes are converted to regular Meshes.
 */
function createStaticCopy(object: THREE.Group): THREE.Group {
  const staticGroup = new THREE.Group();
  staticGroup.name = object.name + '_static';
  staticGroup.position.copy(object.position);
  staticGroup.rotation.copy(object.rotation);
  staticGroup.scale.copy(object.scale);

  // Recursively process children
  function processNode(source: THREE.Object3D, target: THREE.Object3D) {
    for (const child of source.children) {
      let newChild: THREE.Object3D;

      if (child instanceof THREE.SkinnedMesh) {
        // Convert SkinnedMesh to regular Mesh
        newChild = convertSkinnedMeshToMesh(child);
      } else if (child instanceof THREE.Mesh) {
        // Clone regular meshes
        newChild = child.clone();
      } else if (child instanceof THREE.Bone) {
        // Skip bones in static copy
        continue;
      } else if (child instanceof THREE.Group) {
        // Clone groups and process their children
        newChild = new THREE.Group();
        newChild.name = child.name;
        newChild.position.copy(child.position);
        newChild.rotation.copy(child.rotation);
        newChild.scale.copy(child.scale);
        processNode(child, newChild);
      } else {
        // For other object types, try to clone or skip
        if ('clone' in child && typeof child.clone === 'function') {
          newChild = child.clone();
        } else {
          continue;
        }
      }

      target.add(newChild);
    }
  }

  processNode(object, staticGroup);

  return staticGroup;
}

/**
 * Collects all animations from the object hierarchy and the object itself.
 */
function collectAnimations(object: THREE.Group): THREE.AnimationClip[] {
  const animations: THREE.AnimationClip[] = [];
  const seen = new Set<string>();

  // Check animations directly on the loaded object (FBX stores them here)
  if ((object as any).animations && Array.isArray((object as any).animations)) {
    for (const anim of (object as any).animations) {
      if (!seen.has(anim.uuid)) {
        animations.push(anim);
        seen.add(anim.uuid);
      }
    }
  }

  // Also traverse the hierarchy
  object.traverse((child) => {
    if (child.animations && child.animations.length > 0) {
      for (const anim of child.animations) {
        if (!seen.has(anim.uuid)) {
          animations.push(anim);
          seen.add(anim.uuid);
        }
      }
    }
  });

  return animations;
}

export const onLoad = (
  object: THREE.Group,
  extraData?: {
    textureCache?: Map<string, THREE.Texture>;
    loaderWarnings?: string[];
    fileUrlCache?: Map<string, string>;
  },
): LoadResult => {
  let hasMeshes = false;
  let removedTextures = false;
  const missingTextures: string[] = [];
  const textureUrls = new Map<string, string>();
  const textureAssignmentWarnings: string[] = [];

  // Pre-pass: collect UUIDs of already-assigned textures
  const usedTextureUuids = new Set<string>();

  console.log('postprocessing start');

  object.position.set(0, 0, 0);

  // Store file URLs in loadedFilesStore metadata for later download during export
  // First check if __fileUrlCache is attached to the object (from loader)
  const fileUrlCache = (object as any).__fileUrlCache || extraData?.fileUrlCache;
  if (fileUrlCache) {
    loadedFilesStore.fileUrlCache = new Map(fileUrlCache);
    console.log(`Loaded ${loadedFilesStore.fileUrlCache.size} file URLs for export`);
  }

  // Collect already-used texture UUIDs
  object.traverse((child) => {
    if ((child as THREE.Mesh).isMesh) {
      const mesh = child as THREE.Mesh;
      const mat = mesh.material;
      const mats = Array.isArray(mat) ? mat : [mat];

      mats.forEach((m) => {
        if ((m as THREE.MeshStandardMaterial).map) {
          usedTextureUuids.add((m as THREE.MeshStandardMaterial).map!.uuid);
        }
        if ((m as THREE.MeshStandardMaterial).aoMap) {
          usedTextureUuids.add((m as THREE.MeshStandardMaterial).aoMap!.uuid);
        }
      });
    }
  });

  object.traverse(function (child) {
    if (child instanceof THREE.Mesh) {
      const mesh = child as THREE.Mesh;
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];

      // Process each material
      materials.forEach((material) => {
        const stdMaterial = material as THREE.MeshStandardMaterial;

        // Step 1: Orphan texture recovery FIRST - recover missing textures before applying material properties
        if (!stdMaterial.map && extraData?.textureCache) {
          // Get unique textures from cache (filter out duplicate entries with different keys)
          const uniqueTextures = new Map<string, { url: string; texture: THREE.Texture }>();
          for (const [url, texture] of extraData.textureCache.entries()) {
            if (!uniqueTextures.has(texture.uuid)) {
              uniqueTextures.set(texture.uuid, { url, texture });
            }
          }

          let assigned = false;

          // First try name matching
          for (const { url, texture } of uniqueTextures.values()) {
            const filename = url.split('/').pop() || '';
            if (isNameMatch(mesh.name, filename)) {
              console.log(`Auto-assigning orphaned texture "${filename}" to mesh "${mesh.name}"`);
              const message = `Auto-assigning orphaned texture "${filename}" to mesh "${mesh.name}"`;
              textureAssignmentWarnings.push(message);
              stdMaterial.map = texture;
              stdMaterial.map.colorSpace = THREE.SRGBColorSpace;
              assigned = true;
              break;
            }
          }

          // Fallback: if only one unique texture and it's not used elsewhere, assign it
          // This handles FBX files where AmbientColor map was skipped but texture was loaded
          if (!assigned && uniqueTextures.size > 0) {
            const [{ url, texture }] = [...uniqueTextures.values()];
            const filename = url.split('/').pop() || '';
            if (!usedTextureUuids.has(texture.uuid)) {
              const message = `Auto-assigning single orphaned texture "${filename}" to mesh "${mesh.name}" (AmbientColor workaround)`;
              console.log(message);
              textureAssignmentWarnings.push(message);
              stdMaterial.map = texture;
              stdMaterial.map.colorSpace = THREE.SRGBColorSpace;
            }
          }
        }

        // Step 2: Check if material has unloaded textures and remove them
        if ('map' in material && material.map !== null) {
          const texture = material.map as THREE.Texture;
          const source = texture.source;

          // Если у текстуры вообще нет данных (даже попытки загрузки)
          const hasNoData = !source || !source.data;

          // Дополнительно проверяем, не является ли это "пустой" текстурой-заглушкой
          // которую Three.js иногда создает при ошибках
          if (hasNoData) {
            const textureName = texture.name || texture.userData?.filename || 'Unknown';
            removedTextures = true;
            if (!missingTextures.includes(textureName)) {
              missingTextures.push(textureName);
            }
            material.map = null; // Удаляем только если данных действительно нет
          }
        }

        // Step 3: Apply material properties - fix rendering issues
        stdMaterial.transparent = false;
        stdMaterial.opacity = 1.0;
        stdMaterial.depthWrite = true;
        stdMaterial.depthTest = true;
        stdMaterial.side = THREE.DoubleSide;

        // Disable vertex colors that cause black silhouettes
        //stdMaterial.vertexColors = false;
        stdMaterial.vertexColors = !!mesh.geometry.attributes.color;
        // Force PBR defaults to avoid dark metallic looks
        stdMaterial.metalness = 0;
        stdMaterial.roughness = 0.6;

        if (stdMaterial.map) {
          stdMaterial.map.format = THREE.RGBAFormat;
        }

        // Step 4: Apply emissive fix LAST - only after texture recovery
        if (stdMaterial.map && stdMaterial.emissive) {
          stdMaterial.emissiveMap = stdMaterial.map;
          stdMaterial.emissive.setHex(0x666666);
          stdMaterial.emissiveIntensity = 1.0;
        }

        stdMaterial.needsUpdate = true;
      });

      hasMeshes = true;
      child.geometry.computeBoundingBox();
      child.castShadow = true;
      child.receiveShadow = true;
    }
  });

  // Create an empty bounding box to contain all the objects in the group
  const boundingBox = new THREE.Box3();
  // Iterate through each object in the group and expand the bounding box
  object.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      child.updateWorldMatrix(true, true);
      // Get the child's bounding box in world coordinates
      const childBox = new THREE.Box3().setFromObject(child);
      // Expand the combinedBox to include the child's box
      boundingBox.union(childBox);
    }
  });

  // Create static copy (non-skinned) and collect animations
  const staticObject = createStaticCopy(object);
  const animations = collectAnimations(object);
  const hasSkinned = hasSkinnedMeshes(object);

  console.log(`Loaded model with ${animations.length} animation(s)`);

  return {
    boundingBox,
    object,
    staticObject,
    animations,
    hasMeshes,
    hasSkinnedMesh: hasSkinned,
    removedTextures,
    missingTextures,
    textureUrls,
    requestedTextures: [],
    loadedTextures: [],
    failedTextures: [],
    loaderWarnings: [...(extraData?.loaderWarnings || []), ...textureAssignmentWarnings],
  };
};
export const getLoader = (extension: string) => {
  if (extension === 'fbx') return new EnhancedFBXLoader();
  if (extension === 'obj') return new OBJLoader();
  return null;
};

/**
 * Enhanced FBX loader that handles all texture loading and metadata capture internally.
 * Eliminates the need for separate custom texture loaders.
 */
class EnhancedFBXLoader extends FBXLoader {
  mgr: THREE.LoadingManager;
  sourceNodeUrl?: string;
  private textureInfo: TextureLoadingInfo = {
    requestedTextures: [],
    loadedTextures: [],
    failedTextures: [],
    textureCache: new Map<string, THREE.Texture>(),
  };
  private fileUrlCache: Map<string, string> = new Map(); // Maps filename to download URL
  private loaderWarnings: string[] = [];
  private modelName: string = ''; // Store model name for better filename generation

  constructor(manager?: THREE.LoadingManager) {
    manager = new THREE.LoadingManager();
    super(manager);
    this.mgr = manager;

    // Setup TGA loader
    const tgaLoader = new TGALoader(manager);
    const self = this;
    // Создаем обертку (прокси) для TGALoader
    const smartTgaHandler = {
      // Метод parse нужен для работы со встроенными данными (из буфера)
      parse: (data: ArrayBuffer) => tgaLoader.parse(data),

      // Метод setPath вызывается FBXLoader для установления базовой папки
      setPath: (path: string) => {
        tgaLoader.setPath(path);
        return smartTgaHandler;
      },

      // Метод load вызывается для внешних файлов
      load: (
        url: string,
        onLoad?: (texture: THREE.Texture) => void,
        onProgress?: (event: ProgressEvent) => void,
        onError?: (err: unknown) => void,
      ) => {
        console.log(`[FBXLoader] requested TGA texture: ${url}`);
        // Всегда возвращаем текстуру немедленно, даже если данные загружаются асинхронно
        const texture = new THREE.Texture();

        // Если это уже blob или data - просто грузим
        if (url.startsWith('blob:') || url.startsWith('data:')) {
          return tgaLoader.load(url, onLoad, onProgress, onError);
        }

        // Обертка для обработки ошибок из вложенного tgaLoader.load
        const wrappedOnError = (err: unknown) => {
          if (typeof onError === 'function') {
            onError(err);
          }
        };

        //resolve texture URL
        const textureUrl = resolveTextureUrl(url, self.sourceNodeUrl);

        // Load texture directly
        console.log(`Loading texture: ${textureUrl}`);
        tgaLoader.load(
          url,
          (loadedTexture) => {
            // Copy loaded texture properties to placeholder texture
            (texture as any).isDataTexture = true; // Imprtant for correct loading TGA from url
            texture.image = loadedTexture.image;
            texture.source = loadedTexture.source;
            texture.format = loadedTexture.format;
            texture.type = loadedTexture.type;
            texture.needsUpdate = true;
            if (typeof onLoad === 'function') onLoad(texture);
          },
          onProgress,
          wrappedOnError,
        );

        return texture;
      },
    } as unknown as THREE.Loader<unknown, string>;
    manager.addHandler(/\.tga$/i, smartTgaHandler);

    // Capture file URLs during loading
    this.mgr.onProgress = (url, loaded, total) => {
      console.log('Loading file: ' + url + '.\nLoaded ' + loaded + ' of ' + total + ' files.');
      // Extract filename and store URL for later download during export
      let filename = decodeURIComponent(url.split('/').pop() || url);
      // Remove query parameters if present
      filename = filename.split('?')[0];
      // Preprocess texture name
      filename = preprocessTextureName(filename);
      // If filename is empty (endpoint URL with query params), use model name + index or reconstruct from URL
      if (!filename || filename.includes('=')) {
        // Try to extract meaningful name from URL path or use model name as base
        const pathSegments = new URL(url).pathname.split('/').filter((p) => p);
        if (self.modelName && !filename) {
          filename = self.modelName;
        } else if (pathSegments.length > 0) {
          filename = pathSegments.slice(-2).join('_'); // Use last 2 path segments
        } else {
          filename = `model_${loaded}`; // Fallback
        }
      }
      if (!self.fileUrlCache.has(filename)) {
        self.fileUrlCache.set(filename, url);
        console.log(`Registered file URL for export: ${filename}`);
      }
    };

    // Override URL resolution to transform texture URLs and capture metadata
    const originalResolveURL = this.mgr.resolveURL;
    this.mgr.resolveURL = (url: string) => {
      let resolved = originalResolveURL.call(this.mgr, url);
      resolved = resolveTextureUrl(resolved, this.sourceNodeUrl);
      return resolved;
    };
    installTextureLoaderHookOnce();

    // Debug: log when items start/finish loading
    this.mgr.onStart = (url, itemsLoaded, itemsTotal) => {
      console.debug('Started loading:', url, `(${itemsLoaded}/${itemsTotal})`);
    };

    this.mgr.onError = (url) => {
      console.error('Error loading:', url);
      // Check if this is a texture URL and add to failed textures list
      if (/(\.png|\.jpg|\.jpeg|\.tga|\.bmp|\.gif|\.tiff|\.tif|\.webp|\.psd)(\?|$)/i.test(url)) {
        try {
          const urlObj = new URL(url, window.location.href);
          let filename = decodeURIComponent(urlObj.pathname.split('/').pop() || url);
          // Preprocess texture name
          filename = preprocessTextureName(filename);
          if (filename && !self.textureInfo.failedTextures.includes(filename)) {
            console.warn(`Texture failed to load (404 or other error): ${filename}`);
            self.textureInfo.failedTextures.push(filename);
          }
        } catch {
          // If URL parsing fails, try to extract filename directly
          let filename = url.split('/').pop()?.split('?')[0] || url;
          // Preprocess texture name
          filename = preprocessTextureName(filename);
          if (filename && !self.textureInfo.failedTextures.includes(filename)) {
            self.textureInfo.failedTextures.push(filename);
          }
        }
      }
    };
  }

  getTextureInfo(): TextureLoadingInfo {
    return { ...this.textureInfo };
  }

  getFileUrlCache(): Map<string, string> {
    return new Map(this.fileUrlCache);
  }

  /**
   * Overrides base parse method to inject guards for problematic FBX cases
   * before they trigger application crashes.
   */
  parse(data: ArrayBuffer | string, path: string): THREE.Group {
    const self = this;
    const OriginalObject3D = THREE.Object3D;
    const originalGetObjectByName = OriginalObject3D.prototype.getObjectByName;
    const fixedMeshes = new Set<string>();

    (OriginalObject3D.prototype as any).getObjectByName = function (name: string) {
      const result = originalGetObjectByName.call(this, name);

      // AnimationParser.generateMorphTrack accesses .morphTargetDictionary of the returned object
      // if it's missing or if the object itself is missing, it crashes the entire loading process.
      if (!result) {
        // Only return a dummy object if called specifically from problematic generateMorphTrack
        const stack = new Error().stack;
        if (stack && stack.includes('generateMorphTrack')) {
          console.warn(
            `[EnhancedFBXLoader] Mesh "${name}" not found during morph track generation. Using dummy object to prevent crash.`,
          );
          return new THREE.Group();
        }
      } else if (!(result as any).morphTargetDictionary) {
        // If mesh exists but dictionary is missing, fill it with an empty object
        const stack = new Error().stack;
        if (stack && stack.includes('generateMorphTrack')) {
          (result as any).morphTargetDictionary = {};
          if (!fixedMeshes.has(name)) {
            fixedMeshes.add(name);
            const message = `Skipping missing blend shapes on mesh "${name}" (morphTargetDictionary undefined)`;
            if (!self.loaderWarnings.includes(message)) {
              self.loaderWarnings.push(message);
              console.warn(`[EnhancedFBXLoader] ${message}`);
            }
          }
        }
      }

      return result;
    };

    try {
      return super.parse(data, path);
    } finally {
      // Always restore original behavior
      (OriginalObject3D.prototype as any).getObjectByName = originalGetObjectByName;
    }
  }

  async loadAsync(
    url: string,
    onProgress?: (event: ProgressEvent) => void,
  ): Promise<THREE.Group<THREE.Object3DEventMap>> {
    // Reset texture tracking and warnings for new load
    this.textureInfo = {
      requestedTextures: [],
      loadedTextures: [],
      failedTextures: [],
      textureCache: new Map(),
    };
    this.loaderWarnings = [];
    this.sourceNodeUrl = url;
    // Extract model name from URL for better filename generation
    this.modelName = decodeURIComponent(url.split('/').pop()?.split('?')[0] || 'model');

    // Intercept console.warn to capture PSD and Ambient texture warnings from FBXLoader
    const originalWarn = console.warn;
    const self = this;
    console.warn = function (...args: unknown[]) {
      const message = args.join(' ');

      // Capture any THREE.FBXLoader warnings
      const isFBXWarning = message.includes('THREE.FBXLoader') || message.includes('FBXLoader');
      if (isFBXWarning) {
        // Extract the actual warning message, filtering out formatting placeholders and file paths
        const warningText = message
          .replace(/^[^:]*:\s*/, '') // Remove prefix like "loaders.ts:123"
          .replace(/\s*%s\s*/g, ' ') // Remove %s formatting placeholders
          .replace(/^THREE\.FBXLoader:\s*/i, '') // Remove THREE.FBXLoader: prefix
          .trim();

        if (warningText && !self.loaderWarnings.includes(warningText)) {
          self.loaderWarnings.push(warningText);
          console.log(`[Captured FBX warning] ${warningText}`);
        }
      }

      // Capture PSD texture warnings: "FBXLoader: PSD textures are not supported, creating placeholder texture for ..."
      const psdMatch = message.match(
        /FBXLoader:\s*PSD textures are not supported.*?for\s+(.+?)(?:\s*$|\s*\.psd)/i,
      );
      if (psdMatch) {
        // Extract the full path including .psd extension
        const fullPath = message.match(/for\s+(.+\.psd)/i);
        let texturePath = fullPath ? fullPath[1] : psdMatch[1] + '.psd';
        // Preprocess texture name
        texturePath = preprocessTextureName(texturePath);
        if (!self.textureInfo.requestedTextures.includes(texturePath)) {
          self.textureInfo.requestedTextures.push(texturePath);
        }
        if (!self.textureInfo.failedTextures.includes(texturePath)) {
          self.textureInfo.failedTextures.push(texturePath);
        }
      }

      // Capture Ambient texture warnings: "FBXLoader: AmbientColor map is not supported in three.js, skipping texture."
      const ambientMatch = message.match(/FBXLoader:\s*AmbientColor map is not supported/i);
      if (ambientMatch) {
        console.log('Detected skipped ambient texture in FBXLoader');
        // Mark that we encountered an ambient map that wasn't loaded
        if (!self.textureInfo.requestedTextures.includes('ambient')) {
          self.textureInfo.requestedTextures.push('ambient');
        }
        if (!self.textureInfo.failedTextures.includes('ambient')) {
          self.textureInfo.failedTextures.push('ambient');
        }
      }

      originalWarn.apply(console, args);
    };

    const previousTextureCaptureContext = activeTextureCaptureContext;
    activeTextureCaptureContext = {
      textureInfo: this.textureInfo,
      loggedUrls: new Set<string>(),
    };

    try {
      const result = await super.loadAsync(url, onProgress);
      await this.waitForAllAssetsLoaded();
      // Attach texture cache to result for post-processing with onLoad
      (result as any).__textureCache = this.textureInfo.textureCache;
      // Attach file URLs for downloading during export
      (result as any).__fileUrlCache = this.fileUrlCache;
      // Attach texture loading info
      (result as any).__textureInfo = this.getTextureInfo();
      // Attach loader warnings
      (result as any).__loaderWarnings = this.loaderWarnings;
      return result;
    } finally {
      activeTextureCaptureContext = previousTextureCaptureContext;
      console.warn = originalWarn;
    }
  }

  waitForAllAssetsLoaded() {
    return new Promise((resolve) => {
      console.log('Waiting for all assets to load...', this.mgr);

      // Preserve the original error handler from constructor
      const originalOnError = this.mgr.onError;

      this.mgr.onLoad = () => {
        console.log('All assets loaded');
        resolve(null);
      };

      this.mgr.onError = (url: string) => {
        // Call original error handler to capture failed textures
        if (originalOnError) {
          originalOnError.call(this.mgr, url);
        }
        console.log('There were some errors while loading:', url);
      };
    });
  }
}
